import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { isAuth, getUserName, getUserStatus, getIsSubscribed } from 'containers/authorization/selectors';
import { useDispatch } from 'hooks';
import { logout as logoutAction } from 'containers/authorization/actions';
import Logo from 'assets/svg/FintoolBlack';
import AiSpark from 'assets/svg/AiSpark';
import SearchBar from '../SearchBar';

import { getDesktopHeader } from './utils';
import NavButton from './NavButton';
import UserChip from './Common/UserChip';
import {
  Container,
  LeftGroup,
  LogoLink,
  TabsWrapper,
  RightGroup,
  AiWrapper,
  AiButton,
  AiBadge,
  OutlineNavButton,
} from './styles';

const DesktopHeader = ({ resetSearch, onOpenAi, hasNew }) => {
  const isSignedIn = useSelector(isAuth);
  const status = useSelector(getUserStatus);
  const isSubscribed = useSelector(getIsSubscribed);
  const userName = useSelector(getUserName);
  const { t } = useTranslation('app');

  const handleLogout = useDispatch(logoutAction);

  const leftNav = useMemo(() => getDesktopHeader(isSignedIn, status, isSubscribed)[0], [isSignedIn, status, isSubscribed]);

  const initials = useMemo(() => {
    if (!userName) return '';
    const parts = userName.trim().split(' ');

    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();

    return userName.slice(0, 2).toUpperCase();
  }, [userName]);

  const firstName = useMemo(() => userName?.split(' ')[0] || '', [userName]);

  return (
    <Container>
      <LeftGroup>
        <LogoLink to="/" aria-label="Fintool">
          <Logo />
        </LogoLink>
        <TabsWrapper>
          {leftNav.map(({ key, name, link, matches }) => (
            <NavButton
              key={key}
              name={t(name)}
              link={link}
              onClick={link === '/videos' ? resetSearch : undefined}
              matches={matches}
            />
          ))}
        </TabsWrapper>
      </LeftGroup>
      <RightGroup>
        <SearchBar />
        {isSubscribed && (
          <AiWrapper>
            <AiButton type="button" onClick={onOpenAi}>
              <AiSpark />
              {t('Fintool AI')}
            </AiButton>
            <AiBadge $hasNew={hasNew}>NEU</AiBadge>
          </AiWrapper>
        )}
        {isSignedIn ? (
          <UserChip initials={initials} firstName={firstName} status={status} onLogout={handleLogout} />
        ) : (
          <>
            <OutlineNavButton to="/anmelden">{t('Login')}</OutlineNavButton>
            <OutlineNavButton to="/registrieren">{t('Register')}</OutlineNavButton>
          </>
        )}
      </RightGroup>
    </Container>
  );
};

DesktopHeader.propTypes = {
  resetSearch: PropTypes.func.isRequired,
  onOpenAi: PropTypes.func.isRequired,
  hasNew: PropTypes.bool,
};

DesktopHeader.defaultProps = {
  hasNew: false,
};

export default DesktopHeader;
